import { useState, useEffect } from 'react';
import {
  getContracts, getRenewals, createContract, updateContract, deleteContract,
  getClients, getUserDirectory, getCRMLeads, getContractDocument,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../utils/toastConfig';
import {
  FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiEye, FiFileText,
  FiUser, FiDollarSign, FiCalendar, FiInfo, FiDownload, FiExternalLink,
} from 'react-icons/fi';
import './crmPages.css';

const STATUS_OPTIONS = ['Draft', 'Sent', 'Signed', 'Active', 'Expired', 'Cancelled', 'Renewed'];
const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP'];
const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const formatMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

function statusBadgeClass(status) {
  return 'cp-badge cp-badge--' + String(status || 'draft').toLowerCase();
}

const emptyNewClient = {
  client_name: '',
  address: '',
  industry: '',
  company_size: '',
  gst_number: '',
};

const emptyForm = {
  lead_id: '',
  contract_code: '',
  title: '',
  client_id: '',
  contract_value: '',
  currency: 'INR',
  start_date: '',
  end_date: '',
  renewal_date: '',
  auto_renew: false,
  status: 'Draft',
  payment_terms: '',
  description: '',
  notes: '',
  owner_id: '',
  new_client: { ...emptyNewClient },
  document: null,
};

export default function Contracts() {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo' || user?.role === 'admin';
  const isHR  = user?.role === 'hr';

  const [tab, setTab] = useState('all'); // 'all' | 'renewals'
  const [contracts, setContracts] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1); // 1 = company details, 2 = contract details (create flow only)
  const [viewing, setViewing] = useState(null); // contract being viewed in detail modal

  const loadAll = async () => {
    setLoading(true);
    try {
      // Sales users don't need the directory (they can't assign owners)
      const isSalesUser = user?.role === 'sales';
      const calls = [
        getContracts(),
        getRenewals(60),
        getClients(),
        isSalesUser ? Promise.resolve({ data: [] }) : getUserDirectory(),
        getCRMLeads(),
      ];
      // Use allSettled so one failing call never blocks the rest
      const [c, r, cl, u, l] = await Promise.allSettled(calls);
      setContracts(c.status === 'fulfilled' ? (c.value.data || []) : []);
      setRenewals(r.status === 'fulfilled' ? (r.value.data || []) : []);
      setClients(cl.status === 'fulfilled' ? (cl.value.data || []) : []);
      setUsers((u.status === 'fulfilled' ? (u.value.data || []) : []).filter((x) => x.role === 'sales'));
      setLeads(l.status === 'fulfilled' ? (l.value.data || []) : []);
      // Surface errors only if the primary contracts call failed
      if (c.status === 'rejected') {
        showErrorToast('Failed to load contracts');
      }
    } catch (err) {
      showErrorToast('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, new_client: { ...emptyNewClient } });
    setStep(1);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      contract_code: c.contract_number || '',
      title: c.title || '',
      client_id: c.client_id || '',
      contract_value: c.contract_value || '',
      currency: c.currency || 'INR',
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      end_date: c.end_date ? c.end_date.slice(0, 10) : '',
      renewal_date: c.renewal_date ? c.renewal_date.slice(0, 10) : '',
      auto_renew: !!c.auto_renew,
      status: c.status || 'Draft',
      payment_terms: c.payment_terms || '',
      description: c.description || '',
      notes: c.notes || '',
      owner_id: c.owner_id || '',
      new_client: { ...emptyNewClient },
    });
    setStep(2); // edit skips company-details step
    setShowModal(true);
  };

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleNewClientChange = (k, v) =>
    setForm((f) => ({ ...f, new_client: { ...f.new_client, [k]: v } }));

  const handleLeadPick = (leadId) => {
    if (!leadId) {
      setForm((f) => ({ ...f, lead_id: '' }));
      return;
    }
    const lead = leads.find((l) => String(l.id) === String(leadId));
    if (!lead) return;
    setForm((f) => ({
      ...f,
      lead_id: leadId,
      new_client: {
        ...f.new_client,
        // Pre-fill company name from the lead (empty fields only; don't overwrite user input)
        client_name: f.new_client.client_name || lead.company || lead.name || '',
      },
    }));
  };

  const validateStep1 = () => {
    if (!form.lead_id) return 'Please select the lead you are converting';
    const nc = form.new_client;
    if (!nc.client_name.trim()) return 'Company Name is required';
    if (!nc.address.trim())     return 'Location is required';
    if (!nc.industry.trim())    return 'Industry is required';
    if (!nc.company_size)       return 'Company Size is required';
    const gst = nc.gst_number.trim();
    if (!gst)                   return 'GST Number is required';
    if (gst.length !== 15)      return 'GST Number must be 15 characters';
    return null;
  };

  const goToStep2 = () => {
    const err = validateStep1();
    if (err) { showErrorToast(err); return; }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showErrorToast('Contract Title is required'); return; }
    if (!editing && !form.contract_code.trim()) {
      showErrorToast('Contract Code is required');
      return;
    }
    if (!editing && !form.document) {
      showErrorToast('Contract Document is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          title: form.title,
          client_id: form.client_id || null,
          contract_value: form.contract_value,
          currency: form.currency,
          start_date: form.start_date,
          end_date: form.end_date,
          renewal_date: form.renewal_date,
          auto_renew: form.auto_renew,
          status: form.status,
          payment_terms: form.payment_terms,
          description: form.description,
          notes: form.notes,
          owner_id: form.owner_id || null,
        };
        await updateContract(editing.id, payload);
        showSuccessToast('Contract updated');
      } else {
        const nc = form.new_client;
        const fd = new FormData();
        fd.append('contract_code', form.contract_code.trim());
        fd.append('title', form.title);
        fd.append('contract_value', form.contract_value || '');
        fd.append('currency', form.currency);
        fd.append('start_date', form.start_date || '');
        fd.append('end_date', form.end_date || '');
        fd.append('renewal_date', form.renewal_date || '');
        fd.append('auto_renew', form.auto_renew ? 'true' : 'false');
        fd.append('status', form.status);
        fd.append('payment_terms', form.payment_terms || '');
        fd.append('description', form.description || '');
        fd.append('notes', form.notes || '');
        if (form.owner_id) fd.append('owner_id', form.owner_id);
        if (form.lead_id) fd.append('lead_id', form.lead_id);
        fd.append('new_client', JSON.stringify({
          client_name: nc.client_name.trim(),
          address: nc.address.trim(),
          gst_number: nc.gst_number.trim().toUpperCase(),
          industry: nc.industry.trim(),
          company_size: nc.company_size,
        }));
        fd.append('document', form.document);
        await createContract(fd);
        showSuccessToast('Contract created and client added');
      }
      setShowModal(false);
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openView = (c) => setViewing(c);
  const closeView = () => setViewing(null);

  const openDocument = async (contractId) => {
    try {
      const res = await getContractDocument(contractId);
      const blob = new Blob([res.data], { type: res.data.type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) showErrorToast('Allow pop-ups to view the document');
      // Give the browser a moment to load, then revoke
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Failed to open document');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete contract "${c.contract_number}"?`)) return;
    try {
      await deleteContract(c.id);
      showSuccessToast('Contract deleted');
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Delete failed');
    }
  };

  const list = tab === 'renewals' ? renewals : contracts;

  // Stats
  const active     = contracts.filter((c) => c.status === 'Active' || c.status === 'Signed').length;
  const totalValue = contracts
    .filter((c) => c.status === 'Active' || c.status === 'Signed')
    .reduce((s, c) => s + Number(c.contract_value || 0), 0);
  const expiringSoon = renewals.length;

  return (
    <div className="cp-shell">
      <main className="cp-main">
        <div className="cp-page-header">
          <div>
            <h1>Contracts</h1>
            <p>Manage MSAs, SOWs, and track renewals in one place.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn cp-btn--ghost" onClick={loadAll} title="Refresh">
              <FiRefreshCw /> Refresh
            </button>
            <button className="cp-btn cp-btn--primary" onClick={openCreate}>
              <FiPlus /> New Contract
            </button>
          </div>
        </div>

        <div className="cp-stats">
          <div className="cp-stat">
            <div className="cp-stat__label">Total Contracts</div>
            <div className="cp-stat__value">{contracts.length}</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat__label">Active / Signed</div>
            <div className="cp-stat__value">{active}</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat__label">Active Value</div>
            <div className="cp-stat__value">{formatMoney(totalValue)}</div>
          </div>
          <div className="cp-stat">
            <div className="cp-stat__label">Renewing in 60 days</div>
            <div className="cp-stat__value">{expiringSoon}</div>
            <div className="cp-stat__sub">Click "Renewals" tab to view</div>
          </div>
        </div>

        <div className="cp-tabs">
          <button className={'cp-tab' + (tab === 'all' ? ' cp-tab--active' : '')} onClick={() => setTab('all')}>
            All Contracts ({contracts.length})
          </button>
          <button className={'cp-tab' + (tab === 'renewals' ? ' cp-tab--active' : '')} onClick={() => setTab('renewals')}>
            Renewals ({renewals.length})
          </button>
        </div>

        {loading ? (
          <div className="cp-empty">Loading contracts...</div>
        ) : list.length === 0 ? (
          <div className="cp-card cp-empty">
            <div className="cp-empty__title">No contracts yet</div>
            <div>Click "New Contract" to create your first one.</div>
          </div>
        ) : (
          <div className="cp-table-wrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Value</th>
                  <th>Start</th>
                  <th>End</th>
                  {tab === 'renewals' && <th>Days Left</th>}
                  <th>Status</th>
                  {(isCEO || isHR) && <th>Owner</th>}
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 600 }}>{c.contract_number}</td>
                    <td>{c.title}</td>
                    <td>{c.client_name || '—'}</td>
                    <td>{formatMoney(c.contract_value)}</td>
                    <td>{formatDate(c.start_date)}</td>
                    <td>{formatDate(c.end_date)}</td>
                    {tab === 'renewals' && (
                      <td>
                        <span style={{
                          color: c.days_until_end <= 14 ? '#b91c1c' : c.days_until_end <= 30 ? '#c2410c' : '#374151',
                          fontWeight: 600,
                        }}>
                          {c.days_until_end} days
                        </span>
                      </td>
                    )}
                    <td><span className={statusBadgeClass(c.status)}>{c.status}</span></td>
                    {(isCEO || isHR) && <td>{c.owner_full_name || c.owner_name || '—'}</td>}
                    <td>
                      <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={() => openView(c)} title="View">
                        <FiEye />
                      </button>
                      <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={() => openEdit(c)} title="Edit" style={{ marginLeft: 4 }}>
                        <FiEdit2 />
                      </button>
                      {(isCEO || isHR) && (
                        <button className="cp-btn cp-btn--danger cp-btn--sm" onClick={() => handleDelete(c)} title="Delete" style={{ marginLeft: 4 }}>
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {viewing && (
          <div className="cp-modal-backdrop" onClick={closeView}>
            <div className="cp-modal cp-modal--view" onClick={(e) => e.stopPropagation()}>
              <div className="cp-modal__header cp-view__header">
                <div className="cp-view__header-main">
                  <div className="cp-view__number">{viewing.contract_number}</div>
                  <h2 className="cp-view__title">{viewing.title}</h2>
                  <div className="cp-view__meta">
                    <span className={statusBadgeClass(viewing.status)}>{viewing.status}</span>
                    <span className="cp-view__meta-dot">·</span>
                    <span className="cp-view__meta-text">
                      <FiUser style={{ verticalAlign: '-2px', marginRight: 4 }} />
                      {viewing.client_name || 'No client linked'}
                    </span>
                  </div>
                </div>
                <button className="cp-modal__close" onClick={closeView}>×</button>
              </div>

              <div className="cp-view">
                {/* Financial */}
                <section className="cp-view__section">
                  <h3 className="cp-view__section-title">
                    <FiDollarSign /> Financial
                  </h3>
                  <div className="cp-view__grid cp-view__grid--3">
                    <div className="cp-view__item">
                      <div className="cp-view__label">Value</div>
                      <div className="cp-view__value cp-view__value--lg">
                        {viewing.currency || 'INR'} {Number(viewing.contract_value || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="cp-view__item">
                      <div className="cp-view__label">Currency</div>
                      <div className="cp-view__value">{viewing.currency || 'INR'}</div>
                    </div>
                    <div className="cp-view__item">
                      <div className="cp-view__label">Payment Terms</div>
                      <div className="cp-view__value">{viewing.payment_terms || '—'}</div>
                    </div>
                  </div>
                </section>

                {/* Timeline */}
                <section className="cp-view__section">
                  <h3 className="cp-view__section-title">
                    <FiCalendar /> Timeline
                  </h3>
                  <div className="cp-view__grid cp-view__grid--4">
                    <div className="cp-view__item">
                      <div className="cp-view__label">Start Date</div>
                      <div className="cp-view__value">{formatDate(viewing.start_date)}</div>
                    </div>
                    <div className="cp-view__item">
                      <div className="cp-view__label">End Date</div>
                      <div className="cp-view__value">{formatDate(viewing.end_date)}</div>
                    </div>
                    <div className="cp-view__item">
                      <div className="cp-view__label">Renewal Reminder</div>
                      <div className="cp-view__value">{formatDate(viewing.renewal_date)}</div>
                    </div>
                    <div className="cp-view__item">
                      <div className="cp-view__label">Auto-renew</div>
                      <div className="cp-view__value">
                        <span className={'cp-chip ' + (viewing.auto_renew ? 'cp-chip--green' : 'cp-chip--gray')}>
                          {viewing.auto_renew ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Details */}
                {(viewing.description || viewing.notes) && (
                  <section className="cp-view__section">
                    <h3 className="cp-view__section-title">
                      <FiInfo /> Details
                    </h3>
                    {viewing.description && (
                      <div className="cp-view__item" style={{ marginBottom: 12 }}>
                        <div className="cp-view__label">Description</div>
                        <div className="cp-view__value" style={{ whiteSpace: 'pre-wrap' }}>{viewing.description}</div>
                      </div>
                    )}
                    {viewing.notes && (
                      <div className="cp-view__item">
                        <div className="cp-view__label">Notes</div>
                        <div className="cp-view__value" style={{ whiteSpace: 'pre-wrap' }}>{viewing.notes}</div>
                      </div>
                    )}
                  </section>
                )}

                {/* Document */}
                <section className="cp-view__section">
                  <h3 className="cp-view__section-title">
                    <FiFileText /> Contract Document
                  </h3>
                  {viewing.document_path ? (
                    <div className="cp-view__doc-card">
                      <div className="cp-view__doc-icon">
                        <FiFileText size={24} />
                      </div>
                      <div className="cp-view__doc-info">
                        <div className="cp-view__doc-name">{viewing.document_name || 'document'}</div>
                        <div className="cp-view__doc-sub">Click to open in a new tab</div>
                      </div>
                      <div className="cp-view__doc-actions">
                        <button
                          type="button"
                          className="cp-btn cp-btn--primary cp-btn--sm"
                          onClick={() => openDocument(viewing.id)}
                        >
                          <FiExternalLink /> View
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="cp-view__doc-empty">No document attached to this contract.</div>
                  )}
                </section>

                <div className="cp-view__footer">
                  <button type="button" className="cp-btn cp-btn--ghost" onClick={closeView}>Close</button>
                  <button type="button" className="cp-btn cp-btn--primary" onClick={() => { closeView(); openEdit(viewing); }}>
                    <FiEdit2 /> Edit Contract
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="cp-modal-backdrop" onClick={() => !saving && setShowModal(false)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cp-modal__header">
                <h2>{editing ? `Edit ${editing.contract_number}` : 'New Contract'}</h2>
                <button className="cp-modal__close" onClick={() => setShowModal(false)}>×</button>
              </div>

              {!editing && (
                <div className="cp-stepper">
                  <div className={'cp-step' + (step === 1 ? ' cp-step--active' : ' cp-step--done')}>
                    <span className="cp-step__num">1</span>
                    <span className="cp-step__label">Company Details</span>
                  </div>
                  <div className="cp-step__divider" />
                  <div className={'cp-step' + (step === 2 ? ' cp-step--active' : '')}>
                    <span className="cp-step__num">2</span>
                    <span className="cp-step__label">Contract Details</span>
                  </div>
                </div>
              )}

              {/* Step 1: Company Details (create flow only) */}
              {!editing && step === 1 && (
                <form
                  className="cp-form"
                  onSubmit={(e) => { e.preventDefault(); goToStep2(); }}
                >
                  <div className="cp-field cp-field--full">
                    <label>Lead *</label>
                    <select
                      value={form.lead_id}
                      onChange={(e) => handleLeadPick(e.target.value)}
                      required
                    >
                      <option value="">— Select the lead you're converting —</option>
                      {leads.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.company ? `${l.company} — ${l.name}` : l.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>Company Name *</label>
                    <input
                      value={form.new_client.client_name}
                      onChange={(e) => handleNewClientChange('client_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>Location *</label>
                    <input
                      placeholder="e.g., Hyderabad, Telangana"
                      value={form.new_client.address}
                      onChange={(e) => handleNewClientChange('address', e.target.value)}
                      required
                    />
                  </div>
                  <div className="cp-field">
                    <label>Industry *</label>
                    <input
                      placeholder="e.g., Education, Technology, Fintech"
                      value={form.new_client.industry}
                      onChange={(e) => handleNewClientChange('industry', e.target.value)}
                      required
                    />
                  </div>
                  <div className="cp-field">
                    <label>Company Size *</label>
                    <select
                      value={form.new_client.company_size}
                      onChange={(e) => handleNewClientChange('company_size', e.target.value)}
                      required
                    >
                      <option value="">— Select size —</option>
                      {COMPANY_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>GST Number *</label>
                    <input
                      maxLength={15}
                      placeholder="15-character GSTIN"
                      value={form.new_client.gst_number}
                      onChange={(e) => handleNewClientChange('gst_number', e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  <div className="cp-form-actions cp-form-actions--split">
                    <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="cp-btn cp-btn--primary">Next →</button>
                  </div>
                </form>
              )}

              {/* Step 2: Contract Details (create + edit) */}
              {(editing || step === 2) && (
                <form className="cp-form" onSubmit={handleSubmit}>
                  {!editing && (
                    <>
                      <div className="cp-field">
                        <label>Contract Code *</label>
                        <input
                          placeholder="e.g., ACME-001"
                          value={form.contract_code}
                          onChange={(e) => handleChange('contract_code', e.target.value)}
                          required
                        />
                      </div>
                      <div className="cp-field">
                        <label>Contract Document *</label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(e) => handleChange('document', e.target.files?.[0] || null)}
                          required
                        />
                        {form.document && (
                          <small style={{ display: 'block', marginTop: 4, color: '#6b7280' }}>
                            {form.document.name} ({(form.document.size / 1024).toFixed(1)} KB)
                          </small>
                        )}
                      </div>
                    </>
                  )}
                  <div className={editing ? 'cp-field cp-field--full' : 'cp-field'}>
                    <label>Contract Status</label>
                    <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>Contract Title *</label>
                    <input value={form.title} onChange={(e) => handleChange('title', e.target.value)} required />
                  </div>
                  <div className="cp-field">
                    <label>Currency</label>
                    <select value={form.currency} onChange={(e) => handleChange('currency', e.target.value)}>
                      {CURRENCY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="cp-field">
                    <label>Contract Value</label>
                    <input type="number" step="0.01" value={form.contract_value} onChange={(e) => handleChange('contract_value', e.target.value)} />
                  </div>
                  {editing && (
                    <div className="cp-field cp-field--full">
                      <label>Client</label>
                      <select value={form.client_id} onChange={(e) => handleChange('client_id', e.target.value)}>
                        <option value="">— Select client —</option>
                        {clients.map((cl) => <option key={cl.id} value={cl.id}>{cl.client_name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="cp-field">
                    <label>Start Date</label>
                    <input type="date" value={form.start_date} onChange={(e) => handleChange('start_date', e.target.value)} />
                  </div>
                  <div className="cp-field">
                    <label>End Date</label>
                    <input type="date" value={form.end_date} onChange={(e) => handleChange('end_date', e.target.value)} />
                  </div>
                  <div className="cp-field">
                    <label>Renewal Reminder Date</label>
                    <input type="date" value={form.renewal_date} onChange={(e) => handleChange('renewal_date', e.target.value)} />
                  </div>
                  <div className="cp-field">
                    <label>Auto-renew</label>
                    <select value={form.auto_renew ? '1' : '0'} onChange={(e) => handleChange('auto_renew', e.target.value === '1')}>
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>Payment Terms</label>
                    <input placeholder="Net 30, etc." value={form.payment_terms} onChange={(e) => handleChange('payment_terms', e.target.value)} />
                  </div>
                  {(isCEO || isHR) && (
                    <div className="cp-field cp-field--full">
                      <label>Assigned To</label>
                      <select value={form.owner_id} onChange={(e) => handleChange('owner_id', e.target.value)}>
                        <option value="">— Unassigned —</option>
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="cp-field cp-field--full">
                    <label>Description</label>
                    <textarea rows={2} value={form.description} onChange={(e) => handleChange('description', e.target.value)} />
                  </div>
                  <div className="cp-field cp-field--full">
                    <label>Notes</label>
                    <textarea rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                  </div>
                  <div className="cp-form-actions cp-form-actions--split">
                    {!editing ? (
                      <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setStep(1)} disabled={saving}>← Back</button>
                    ) : <span />}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                      <button type="submit" className="cp-btn cp-btn--primary" disabled={saving}>
                        {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Convert to Contract')}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
